import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import socket from '@/lib/socket';
import { useNexusStore } from '@/stores/nexusStore';
import { toast } from 'sonner';
import { Wifi, WifiOff } from 'lucide-react';

export default function AppShell() {
  const { setConnected, updateAgentStatus, addActivityEntry, addTicketUpdate, isConnected } = useNexusStore();
  const prevConnected = useRef(isConnected);

  useEffect(() => {
    // Connection handlers
    const onConnect = () => {
      setConnected(true);
      // Only toast on reconnect, not initial connect
      if (prevConnected.current === false) {
        toast.success('Connection restored', {
          description: 'Real-time feed is back online',
          icon: <Wifi size={14} />,
        });
      }
      prevConnected.current = true;
    };

    const onDisconnect = () => {
      setConnected(false);
      prevConnected.current = false;
      toast.error('Connection lost', {
        description: 'Attempting to reconnect...',
        icon: <WifiOff size={14} />,
      });
    };

    // Event handlers
    const onAgentAction = (data) => {
      addActivityEntry(data);

      // Toast for critical severity events
      if (data.severity === 'critical') {
        toast.error(`${data.agent_name}: ${data.action?.slice(0, 80)}`, {
          description: data.ticket_id || '',
        });
      }
    };

    const onAgentStatus = (data) => {
      updateAgentStatus(data);
    };

    const onTicketUpdated = (data) => {
      addTicketUpdate(data);

      // Toast for ticket resolutions
      if (data.status === 'RESOLVED') {
        toast.success(`Ticket ${data.ticket_id} resolved`, {
          description: `Resolved by ${data.resolved_by || 'agent'}`,
        });
      }
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('agent:action', onAgentAction);
    socket.on('agent:status', onAgentStatus);
    socket.on('ticket:updated', onTicketUpdated);

    // Set initial connection state
    if (socket.connected) {
      setConnected(true);
      prevConnected.current = true;
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('agent:action', onAgentAction);
      socket.off('agent:status', onAgentStatus);
      socket.off('ticket:updated', onTicketUpdated);
    };
  }, [setConnected, updateAgentStatus, addActivityEntry, addTicketUpdate]);

  return (
    <div className="flex h-screen bg-[var(--nexus-bg-base)] overflow-hidden" data-testid="app-shell">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <Header />
        {/* Reconnect banner */}
        {!isConnected && (
          <div className="flex items-center justify-center gap-2 px-4 py-1.5 bg-[var(--nexus-danger)] bg-opacity-15 border-b border-red-500/20" data-testid="reconnect-banner">
            <WifiOff size={12} className="text-[var(--nexus-danger)]" />
            <span className="text-[11px] text-red-300 font-mono">Connection lost — reconnecting...</span>
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--nexus-danger)] animate-nexus-pulse-dot" />
          </div>
        )}
        <main className="flex-1 overflow-auto p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
